/**
 * YUMIX — Module de surveillance globale des erreurs.
 *
 * Installation: importé une seule fois dans /app/frontend/src/index.js
 * (ou App.js) pour enregistrer les listeners window.onerror et
 * window.onunhandledrejection. Reporte aussi les erreurs capturées
 * manuellement via ErrorLogger.log(err, 'FeatureName').
 *
 *  - Payload envoyé à POST /api/errors/log
 *  - Throttling local 1 erreur identique / 5 s pour éviter les boucles
 *  - Silent-fail côté réseau : ne jamais casser l'UX même si la route est down
 *  - **Breadcrumbs synchrones en localStorage** : si l'app crashe (OOM noir),
 *    le dernier breadcrumb survit au reload et est envoyé en post-mortem au
 *    prochain démarrage → on voit la dernière ligne de code traversée.
 */
import { api } from '../context/AppContext';

const DEDUPE_WINDOW_MS = 5000;
const _lastSeen = new Map(); // key -> timestamp
const BREADCRUMB_KEY = 'yumix_breadcrumb_v1';
const BREADCRUMB_MAX = 12; // last N hops

// Patterns that are NOT our bug — third-party scripts, browser extensions,
// hardware glitches, network blips. These pollute the Bug Radar with false
// positives that we can't fix anyway. Filter them out at source.
const IGNORE_PATTERNS = [
  // Hardware / OS audio failures — user-side, not a code bug
  /failed to start the audio device/i,
  /audio device/i,
  /the play\(\) request was interrupted/i,
  /the audiocontext was not allowed/i,
  // Browser extensions injecting code
  /extension(s)?:\/\//i,
  /chrome-extension:\/\//i,
  /moz-extension:\/\//i,
  /safari-extension:\/\//i,
  // Safari ghost errors when external scripts fail on flaky connections
  /script error\.?$/i,
  /^script error/i,
  // Resize / layout observers (benign)
  /resizeobserver loop limit exceeded/i,
  /resizeobserver loop completed/i,
  // Browser quota / private mode
  /quotaexceedederror/i,
  // Tab unload aborts mid-fetch
  /the operation was aborted/i,
  /aborterror/i,
  // Chunk loading on flaky mobile networks (we already auto-retry)
  /loading chunk \d+ failed/i,
  /loading css chunk \d+ failed/i,
  // User-side network blips on long-running fetches (5G drop, Wi-Fi switch,
  // tab backgrounding mid-stream). The user already sees the spinner and our
  // axios interceptor auto-retries. Logging these to Bug Radar created 80%
  // noise (Tetou's Coach YUMIX stream errors Mai 2026).
  /^typeerror:?\s*(network error|networkerror|failed to fetch|load failed)$/i,
  /^typeerror:?\s*the network connection was lost/i,
  /^network ?error when attempting to fetch/i,
  // Plain "network error" (no TypeError prefix) — same root cause
  /^network ?error$/i,
  // Service Worker update churn — Android Chrome aborts updates frequently
  // when the app is backgrounded. Not actionable.
  /failed to update a serviceworker/i,
];

function shouldIgnore(error) {
  const msg = String(error?.message || error || '');
  if (!msg) return false;
  return IGNORE_PATTERNS.some((p) => p.test(msg));
}

function makeKey(error, feature) {
  const msg = (error?.message || String(error || '')).slice(0, 120);
  return `${feature}::${msg}`;
}

const ErrorLogger = {
  /**
   * Synchronous breadcrumb writer — survives a page reload.
   * Use it to mark "I'm about to do X" before any heavy step.
   */
  breadcrumb(label, extra = undefined) {
    try {
      const raw = localStorage.getItem(BREADCRUMB_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      arr.push({ label: String(label).slice(0, 100), extra: extra || null, at: Date.now() });
      while (arr.length > BREADCRUMB_MAX) arr.shift();
      localStorage.setItem(BREADCRUMB_KEY, JSON.stringify(arr));
    } catch { /* localStorage full / private mode → silent */ }
  },

  /** Clear breadcrumbs after they've been confirmed sent (or on success path). */
  clearBreadcrumbs() {
    try { localStorage.removeItem(BREADCRUMB_KEY); } catch { /* noop */ }
  },

  async log(error, feature = 'unknown', extra = undefined) {
    try {
      // Drop third-party / hardware / ghost errors — they're not our bug
      // and clutter the Bug Radar (Mai 2026 — Yuri spec).
      if (shouldIgnore(error)) return;

      // Normalise error shape
      const errObj = error instanceof Error
        ? error
        : (typeof error === 'object' && error !== null)
          ? error
          : { message: String(error || 'unknown error') };
      const key = makeKey(errObj, feature);
      const now = Date.now();
      const last = _lastSeen.get(key) || 0;
      if (now - last < DEDUPE_WINDOW_MS) return;
      _lastSeen.set(key, now);

      // Attach the latest breadcrumb trail so the backend log shows the hop list.
      let crumbs = null;
      try {
        const raw = localStorage.getItem(BREADCRUMB_KEY);
        if (raw) crumbs = JSON.parse(raw).slice(-BREADCRUMB_MAX);
      } catch { /* noop */ }

      const payload = {
        error_message: (errObj.message || 'unknown').slice(0, 2000),
        stack: (errObj.stack || '').slice(0, 8000) || null,
        feature: String(feature || 'unknown').slice(0, 80),
        severity: (extra && extra.severity) ? String(extra.severity).toUpperCase().slice(0, 16) : 'ERROR',
        device: (typeof navigator !== 'undefined' ? navigator.userAgent : '').slice(0, 400),
        url: (typeof window !== 'undefined' ? window.location.href : '').slice(0, 400),
        extra: { ...(extra || {}), breadcrumbs: crumbs },
        client_timestamp: new Date().toISOString(),
      };

      // Fire and forget — never await a failure back to the caller. Uses the
      // shared `api` instance so it carries the session token automatically.
      try {
        await api.post('/errors/log', payload);
      } catch (sendError) {
        // Last-resort console, swallowed so we never re-trigger ourselves
        // eslint-disable-next-line no-console
        console.warn('[YUMIX ErrorLogger] could not send:', sendError?.message || sendError);
      }
    } catch (_swallow) {
      /* never throw from the logger itself */
    }
  },

  /** Registers global handlers. Call once at app startup. */
  install() {
    if (typeof window === 'undefined') return;
    if (window.__yumixErrorLoggerInstalled) return;
    window.__yumixErrorLoggerInstalled = true;

    window.addEventListener('error', (ev) => {
      // Pre-filter: Safari often fires "Script error." with no useful info
      // for cross-origin scripts. Skip filename-only ghost errors too.
      const msg = ev.message || '';
      if (!msg || /^Script error\.?$/i.test(msg)) return;
      const e = ev.error || { message: msg, stack: `${ev.filename}:${ev.lineno}:${ev.colno}` };
      ErrorLogger.log(e, 'Global_Window_Error');
    });

    window.addEventListener('unhandledrejection', (ev) => {
      const reason = ev.reason || { message: 'Unhandled rejection' };
      ErrorLogger.log(reason, 'Async_Promise_Error');
    });

    // 🩺 Post-mortem: if we have leftover breadcrumbs from a previous session
    // that ended in a crash (page reload after black-screen OOM), send them
    // now so the backend has a record of the last action chain.
    //
    // Iter. 404.d — Two-fold hardening to kill the phantom Post_Mortem_Crash
    // reports observed in production:
    //   1. **Clean-exit marker**: hook `beforeunload` to flag the next load
    //      as "intentional reload". If the marker is present at startup, we
    //      DON'T fire post-mortem (the user just navigated away or refreshed
    //      voluntarily — not a crash).
    //   2. **High-risk label whitelist**: only fire post-mortem if the last
    //      breadcrumb is a high-risk operation (active meal analysis, photo
    //      upload, etc.). Background fire-and-forget hints like
    //      `meal:prewarm:dispatched` never deserve a CRITICAL report.
    const CLEAN_EXIT_KEY = 'yumix_clean_exit_v1';
    const HIGH_RISK_LABELS = [
      'meal:analyze:start',
      'meal:stream:open',
      'meal:text-stream:open',
      'photo:upload:start',
      'photo:compress:start',
      'label:scan:start',
      'workout:save:start',
    ];

    // Fire-and-forget clean-exit marker before the page unloads.
    const _markCleanExit = () => {
      try { localStorage.setItem(CLEAN_EXIT_KEY, String(Date.now())); } catch { /* noop */ }
    };
    window.addEventListener('beforeunload', _markCleanExit);
    window.addEventListener('pagehide', _markCleanExit);

    try {
      const cleanExitAt = parseInt(localStorage.getItem(CLEAN_EXIT_KEY) || '0', 10);
      const cleanExitFresh = cleanExitAt && (Date.now() - cleanExitAt) < 60 * 1000;
      try { localStorage.removeItem(CLEAN_EXIT_KEY); } catch { /* noop */ }

      const raw = localStorage.getItem(BREADCRUMB_KEY);
      if (raw && !cleanExitFresh) {
        const crumbs = JSON.parse(raw);
        if (Array.isArray(crumbs) && crumbs.length) {
          const last = crumbs[crumbs.length - 1];
          const ageMs = Date.now() - (last?.at || 0);
          const isHighRisk = HIGH_RISK_LABELS.some((lbl) => last?.label === lbl);
          // Only send if recent (last hour), the label is high-risk, AND we
          // didn't see a clean unload marker.
          if (ageMs < 60 * 60 * 1000 && isHighRisk) {
            ErrorLogger.log(
              { message: `Post-mortem: app reloaded with active breadcrumbs (last="${last?.label}", age=${Math.round(ageMs / 1000)}s)` },
              'Post_Mortem_Crash',
              { severity: 'CRITICAL', breadcrumb_age_ms: ageMs, last_label: last?.label }
            );
          }
          ErrorLogger.clearBreadcrumbs();
        }
      } else if (raw && cleanExitFresh) {
        // Clean reload — silently drop the breadcrumbs, no post-mortem.
        ErrorLogger.clearBreadcrumbs();
      }
    } catch { /* noop */ }

    // eslint-disable-next-line no-console
    console.info('🛡️ [YUMIX] ErrorLogger installed');
  },
};

export default ErrorLogger;
